import { create } from 'zustand';
import WebApp from '@twa-dev/sdk';
import { fetchIdentity, type Enrollment, type Lesson } from '../api/client';

interface AppState {
  studentId: string | null;
  courseId: string | null;
  groupId: string | null;
  studentName: string;
  telegramId: number | null;
  selectedLesson: Lesson | null;
  role: 'student' | 'teacher';
  teacherToken: string | null;
  /**
   * Sayt sessiyasi (Telegramdan tashqarida).
   *
   * `null` va `tgInitData` ham `null` boʻlsa — kirish kerak.
   */
  webToken: string | null;
  tgInitData: string | null;
  coins: number;
  xp: number;
  streak: number;
  centerName: string | null;
  /** Kursda tanga moduli yoqilganmi — sarlavhada chipni ko'rsatish uchun. */
  coinsEnabled: boolean;
  setIds: (studentId: string, courseId: string, groupId?: string) => void;
  setSelectedLesson: (lesson: Lesson | null) => void;
  setStudentName: (name: string) => void;
  setRole: (role: 'student' | 'teacher') => void;
  setTeacherToken: (token: string) => void;
  setWebToken: (token: string | null) => void;
  setGamification: (coins: number, xp: number, streak: number) => void;
  setCenterName: (name: string | null) => void;
  setCoinsEnabled: (v: boolean) => void;
  initFromUrlOrTelegram: () => void;
  /** Serverdan "men kimman" — URL parametrlarini ustidan yozadi. */
  resolveIdentity: (fresh?: boolean) => Promise<void>;
  /** O'quvchi a'zo bo'lgan barcha kurslar. */
  enrollments: Enrollment[];
  /** Kursni tanlash — bir nechta kursda o'qiyotgan o'quvchi uchun. */
  selectCourse: (courseId: string, groupId: string) => void;
  /** Kurslar ro'yxatiga qaytish. */
  clearCourse: () => void;
}

export const useAppStore = create<AppState>((set, get) => ({
  studentId: null,
  courseId: null,
  groupId: null,
  studentName: "O'quvchi",
  telegramId: null,
  selectedLesson: null,
  role: 'student',
  teacherToken: null,
  webToken: (() => {
    try {
      return localStorage.getItem('shaftoli-veb-token');
    } catch {
      return null;
    }
  })(),
  tgInitData: null,
  coins: 0,
  xp: 0,
  streak: 0,
  centerName: null,
  coinsEnabled: false,
  enrollments: [],

  setIds: (studentId, courseId, groupId) =>
    set({ studentId, courseId, ...(groupId && { groupId }) }),
  setSelectedLesson: (selectedLesson) => set({ selectedLesson }),
  setStudentName: (studentName) => set({ studentName }),
  setRole: (role) => set({ role }),
  setTeacherToken: (teacherToken) => set({ teacherToken }),

  /**
   * Sayt sessiyasini saqlaydi (yoki oʻchiradi).
   *
   * `localStorage` — Telegramdan tashqarida sahifa har safar toʻliq
   * qayta yuklanadi, ya'ni xotiradagi holat saqlanmaydi.
   */
  setWebToken: (token: string | null) => {
    try {
      if (token) localStorage.setItem('shaftoli-veb-token', token);
      else localStorage.removeItem('shaftoli-veb-token');
    } catch {
      /* localStorage yopiq boʻlishi mumkin */
    }
    set({ webToken: token });
  },
  setGamification: (coins, xp, streak) => set({ coins, xp, streak }),
  setCenterName: (centerName) => set({ centerName }),
  setCoinsEnabled: (coinsEnabled) => set({ coinsEnabled }),

  /**
   * Rolni va identifikatorlarni SERVERDAN aniqlaydi.
   *
   * `initFromUrlOrTelegram` ularni URL'dan oladi, lekin URL — menyu
   * tugmasiga bir marta yozib qo'yilgan eski qiymat bo'lishi mumkin:
   * u foydalanuvchi `/start` bosmaguncha yangilanmaydi. Ishlab turgan
   * tizimda ustoz o'zini o'quvchi qilib yozganda student manzili ustoz
   * manzilini yozib yubordi va ustoz rejimiga kirishning yo'li qolmadi.
   *
   * Endi haqiqat manbai — initData bo'yicha server javobi. URL faqat
   * ZAXIRA: bu chaqiruv yiqilsa (tarmoq yo'q, eski server), ilova
   * avvalgidek URL bilan ishlayveradi.
   */
  resolveIdentity: async (fresh = false) => {
    try {
      const identity = await fetchIdentity(fresh);
      if (identity.role === 'teacher') {
        set({ role: 'teacher', studentName: identity.name || 'Ustoz' });
        return;
      }

      const enrollments = identity.enrollments || [];

      /*
       * BITTA KURSDA hech narsa o'zgarmaydi — u avtomatik tanlanadi va
       * o'quvchi to'g'ridan-to'g'ri darslar ro'yxatini ko'radi. Kurs
       * tanlagich faqat 2+ kursda chiqadi.
       *
       * Ro'yxat bo'sh bo'lsa (eski yozuv, a'zolik hali yaratilmagan)
       * serverning `courseId` javobi ishlatiladi.
       */
      const single = enrollments.length === 1 ? enrollments[0] : null;

      set({
        role: 'student',
        studentId: identity.studentId,
        enrollments,
        courseId:
          single?.courseId ?? (enrollments.length ? null : identity.courseId),
        groupId:
          single?.groupId ?? (enrollments.length ? null : identity.groupId),
        ...(identity.name ? { studentName: identity.name } : {}),
        /*
         * SAYTDA BU YAGONA MANBA.
         *
         * Telegram ichida `telegramId` SDK'dan keladi; saytda esa u
         * yoʻq. Ilovaning bir qancha yoʻli (dars roʻyxati, profil)
         * unga tayanadi — `CourseListPage` hatto "Telegram orqali
         * oching" deb toʻsib qoʻyardi, garchi oʻquvchi allaqachon
         * kirgan boʻlsa ham.
         *
         * Mavjud qiymat ustidan YOZILMAYDI: Telegram ichida SDK'niki
         * ustun tursin.
         */
        ...(identity.telegramId && !get().telegramId
          ? { telegramId: Number(identity.telegramId) }
          : {}),
      });
    } catch {
      // Jim: URL'dan olingan qiymatlar o'z kuchida qoladi.
    }
  },

  selectCourse: (courseId, groupId) => set({ courseId, groupId }),
  clearCourse: () => set({ courseId: null, groupId: null }),

  initFromUrlOrTelegram: () => {
    const originalHash =
      (window as any).__ORIGINAL_HASH__ || window.location.hash;
    const originalSearch =
      (window as any).__ORIGINAL_SEARCH__ || window.location.search;

    const searchParams = new URLSearchParams(originalSearch);
    let hashParams = new URLSearchParams('');

    if (originalHash.includes('tgWebAppData=')) {
      hashParams = new URLSearchParams(originalHash.replace(/^#\/?\??/, ''));
    } else if (originalHash.includes('?')) {
      hashParams = new URLSearchParams(originalHash.split('?')[1]);
    }

    const urlStudentId =
      searchParams.get('studentId') || hashParams.get('studentId');
    const urlCourseId =
      searchParams.get('courseId') || hashParams.get('courseId');
    const urlRole = (searchParams.get('role') || hashParams.get('role')) as
      'student' | 'teacher' | null;

    let tgId: number | null = null;
    let isTelegram = false;
    let rawInitData = '';

    try {
      // Try to get Telegram WebApp from window
      const tgWebApp = (window as any).Telegram?.WebApp;
      const twa = tgWebApp || WebApp;

      // Get initData from multiple sources
      rawInitData =
        twa.initData ||
        tgWebApp?.initData ||
        hashParams.get('tgWebAppData') ||
        '';

      // More robust Telegram detection
      const hasTelegramObject = !!tgWebApp;
      const hasInitData = !!rawInitData;
      const platformNotUnknown = twa.platform && twa.platform !== 'unknown';
      const isInIframe = window.self !== window.top;
      const userAgent = navigator.userAgent.toLowerCase();
      const isTelegramUA =
        userAgent.includes('telegram') || userAgent.includes('tgweb');

      if (
        hasTelegramObject ||
        hasInitData ||
        platformNotUnknown ||
        isInIframe ||
        isTelegramUA
      ) {
        isTelegram = true;
      }

      // Try multiple ways to get user ID
      const initDataUnsafe = twa.initDataUnsafe || tgWebApp?.initDataUnsafe;

      if (initDataUnsafe?.user?.id) {
        tgId = initDataUnsafe.user.id;
        const name =
          `${initDataUnsafe.user.first_name || ''} ${initDataUnsafe.user.last_name || ''}`.trim();
        if (name) set({ studentName: name });
      } else if (rawInitData) {
        // Fallback manual parsing
        try {
          const paramsStr = new URLSearchParams(rawInitData);
          const userStr = paramsStr.get('user');
          if (userStr) {
            const userObj = JSON.parse(decodeURIComponent(userStr));
            tgId = userObj.id;
            const name =
              `${userObj.first_name || ''} ${userObj.last_name || ''}`.trim();
            if (name) set({ studentName: name });
          }
        } catch {
          // Silent fail for parsing errors
        }
      }

      set({ tgInitData: rawInitData || null });
    } catch {
      // Silent fail for Telegram SDK errors
    }

    /*
     * TELEGRAMDAN TASHQARIDA — endi bu XATO EMAS.
     *
     * Ilgari bu yerda `document.body.innerHTML` bilan "Access Denied"
     * yozilib, ilova butunlay oʻchirilardi. Ya'ni ilova texnik jihatdan
     * sayt boʻla turib, oʻzini saytda ishlashdan MAN QILARDI.
     *
     * Endi: saqlangan sessiya boʻlsa davom etamiz, boʻlmasa `App`
     * kirish sahifasini koʻrsatadi (`webToken` ham, `tgInitData` ham
     * yoʻq boʻlgan holat).
     */
    if (!isTelegram) {
      set({ telegramId: null, tgInitData: null });
      return;
    }

    set({
      studentId: urlStudentId,
      courseId: urlCourseId,
      telegramId: tgId,
      tgInitData: rawInitData || null,
      role: urlRole === 'teacher' ? 'teacher' : 'student',
    });
  },
}));
